import { useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../../redux/store';
import { fetchUpcomingEventsRequest, setCountryCode } from '../../redux/slices/upcomingEventsSlice';
import type { CombinedEvent } from './types';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';

export default function UpcomingEvents() {
  const dispatch = useDispatch<AppDispatch>();
  const { combinedEvents, loading, error, countryCode } = useSelector(
    (state: RootState) => state.upcomingEvents
  );
  const restaurantId = useSelector((state: RootState) => state.clover.restaurantId);

  const [selectedItem, setSelectedItem] = useState<CombinedEvent | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterMonth, setFilterMonth] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [activeDetailTab, setActiveDetailTab] = useState<string>('behavior');
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState<boolean>(false);

  // Fetch events on mount
  useEffect(() => {
    if (restaurantId) {
      dispatch(fetchUpcomingEventsRequest({ 
        restaurantId,
        countryCode 
      }));
    }
  }, [dispatch, restaurantId, countryCode]);

  // Set filter month to current month by default
  useEffect(() => {
    if (combinedEvents.length > 0 && filterMonth === 'all') {
      const currentDate = new Date();
      const currentMonth = currentDate.getMonth();
      const currentYear = currentDate.getFullYear();
      
      const upcomingItem = combinedEvents.find(item => {
        const itemDate = new Date(item.date);
        const itemMonth = itemDate.getMonth();
        const itemYear = itemDate.getFullYear();
        
        return itemYear > currentYear || 
               (itemYear === currentYear && itemMonth >= currentMonth);
      });
      
      if (upcomingItem) {
        const upcomingMonth = new Date(upcomingItem.date).toLocaleString('default', { month: 'long' });
        setFilterMonth(upcomingMonth);
      }
    }
  }, [combinedEvents, filterMonth]);

  // Auto-select first item
  useEffect(() => {
    if (!selectedItem && combinedEvents.length > 0) {
      setSelectedItem(combinedEvents[0]);
    }
  }, [combinedEvents, selectedItem]);

  // Get unique months for filters
  const months = useMemo(() => {
    const monthList = [...new Set(combinedEvents
      .map(item => {
        const date = new Date(item.date);
        return date.toLocaleString('default', { month: 'long' });
      }))];
    return monthList.sort();
  }, [combinedEvents]);

  // Filter combined items
  const filteredItems = useMemo(() => {
    return combinedEvents.filter(item => {
      const searchMatch = searchTerm === '' || 
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase());
      
      const monthMatch = filterMonth === 'all' || 
        new Date(item.date).toLocaleString('default', { month: 'long' }) === filterMonth;
      
      const categoryMatch = filterCategory === 'all' || item.category === filterCategory;
      
      return searchMatch && monthMatch && categoryMatch;
    });
  }, [combinedEvents, searchTerm, filterMonth, filterCategory]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short',
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTextWithBold = (text: string) => {
    if (!text) return text;
    return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  };

  const handleCountryChange = (newCountryCode: string) => {
    dispatch(setCountryCode(newCountryCode));
    if (restaurantId) {
      dispatch(fetchUpcomingEventsRequest({ 
        restaurantId,
        countryCode: newCountryCode 
      }));
    }
  };

  if (loading) {
    return <LoadingState message="Loading upcoming events..." />;
  }

  if (error) {
    return <ErrorState message={error} />;
  }

  return (
    <div className="flex flex-col h-[83vh] bg-black-50 relative rounded-lg overflow-hidden border border-gray-200">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <h2 className="text-2xl font-bold text-gray-900">Upcoming Events & Holidays</h2>
        <p className="text-gray-600 mt-1">Strategic holiday marketing opportunities and local events</p>
      </div>
      
      <div className="flex flex-1 bg-black-50 relative overflow-hidden">
        {/* Left Side - Events Image */}
        <div className="w-1/3 flex items-center justify-center p-8 border-r border-gray-200 bg-white flex-shrink-0">
          <img 
            src="/assets/images/drawer/events.png" 
            alt="Events"
            className="w-full h-auto max-w-md object-contain"
          />
        </div>

        {/* Right Side - Content */}
        <div className="flex flex-1 relative overflow-hidden">
        {/* Collapse Button */}
        {isLeftPanelCollapsed && (
          <button
            onClick={() => setIsLeftPanelCollapsed(false)}
            className="absolute top-2 left-4 z-10 p-2 bg-white hover:bg-black-100 rounded-lg shadow-md border border-gray-200 transition-colors"
          >
            <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}

        {/* Left Panel - Event List */}
        {!isLeftPanelCollapsed && (
          <div className="w-80 min-w-80 max-w-80 bg-white border-r border-gray-200 flex flex-col transition-all duration-300 flex-shrink-0 h-full">
            {/* Header */}
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Events & Holidays</h2>
                  <p className="text-sm text-gray-600 mt-1">Marketing opportunities tracker</p>
                </div>
                <button
                  onClick={() => setIsLeftPanelCollapsed(true)}
                  className="p-2 hover:bg-black-200 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Search and Filters */}
            <div className="p-6 border-b border-gray-200 space-y-4 flex-shrink-0">
              {/* Search Bar */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
              </div>

              {/* Filters */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <select 
                    value={filterMonth} 
                    onChange={(e) => setFilterMonth(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  >
                    <option value="all">All Months</option>
                    {months.map(month => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Events List */}
            <div className="flex-1 overflow-y-auto" style={{ maxHeight: '360px' }}>
              {filteredItems.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p>No events found</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {filteredItems.map((item) => (
                    <div
                      key={item.id}
                      onClick={() => setSelectedItem(item)}
                      className={`p-4 cursor-pointer hover:bg-black-50 transition-colors ${
                        selectedItem?.id === item.id ? 'bg-orange-50 border-r-2 border-orange-500' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-semibold text-gray-900 truncate">
                            {item.name}
                          </h3>
                          <p className="text-xs text-gray-500 mt-1">
                            {formatDate(item.date)} {item.country_code && `• ${item.country_code}`}
                          </p>
                          <span className={`inline-flex items-center px-2 py-1 mt-2 rounded-full text-xs font-medium ${
                            item.category === 'Holiday' ? 'bg-blue-100 text-blue-800' : 
                            item.category === 'Local Event' ? 'bg-green-100 text-green-800' :
                            'bg-purple-100 text-purple-800'
                          }`}>
                            {item.category}
                          </span>
                        </div>
                        <div className="ml-2 flex-shrink-0">
                          <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Right Panel - Item Details */}
        <div className="flex-1 bg-white flex flex-col overflow-hidden">
          {selectedItem ? (
            <>
              {/* Header */}
              <div className="text-grey-700 p-4 border-b pt-10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div>
                      <h2 className="text-xl font-bold text-gray-900 mb-1">
                        {selectedItem.name}
                      </h2>
                      <div className="flex items-center space-x-3">
                        <div className="flex items-center space-x-2">
                          <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className="text-gray-600 text-sm">
                            {formatDate(selectedItem.date)}
                          </span>
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                            {selectedItem.type}
                          </span>
                          
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                            selectedItem.category === 'Holiday' ? 'bg-blue-100 text-blue-800' : 
                            selectedItem.category === 'Local Event' ? 'bg-green-100 text-green-800' :
                            'bg-purple-100 text-purple-800'
                          }`}>
                            {selectedItem.category}
                          </span>

                          {selectedItem.country_code && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {selectedItem.country_code}
                            </span>
                          )}
                        </div>
                      </div>
                      {selectedItem.venue && (
                        <p className="text-sm text-gray-500 mt-2">📍 {selectedItem.venue}</p>
                      )}
                      {selectedItem.location && (
                        <p className="text-sm text-gray-500 mt-2">📍 {selectedItem.location}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Content Area */}
              <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(83vh - 160px)' }}>
                <div className="p-6 space-y-6">
                  {/* Detail Tabs */}
                  <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 px-6 py-4 border-b border-orange-200">
                      <h2 className="text-lg font-semibold text-orange-800">Event Analysis</h2>
                      <p className="text-sm text-orange-600 mt-1">Marketing insights and opportunities</p>
                    </div>
                    
                    <nav className="flex bg-white border-b border-gray-200">
                      <button
                        onClick={() => setActiveDetailTab('behavior')}
                        className={`flex-1 py-4 px-6 text-sm font-medium transition-all ${
                          activeDetailTab === 'behavior'
                            ? 'text-orange-600 bg-orange-50 border-b-2 border-orange-500'
                            : 'text-gray-600 hover:text-gray-800 hover:bg-black-50'
                        }`}
                      >
                        Consumer Behavior
                      </button>
                      <button
                        onClick={() => setActiveDetailTab('marketing')}
                        className={`flex-1 py-4 px-6 text-sm font-medium transition-all ${
                          activeDetailTab === 'marketing'
                            ? 'text-orange-600 bg-orange-50 border-b-2 border-orange-500'
                            : 'text-gray-600 hover:text-gray-800 hover:bg-black-50'
                        }`}
                      >
                        Marketing Opportunities
                      </button>
                    </nav>

                    {/* Tab Content */}
                    <div className="p-6">
                      {activeDetailTab === 'behavior' && (
                        <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg p-6 border border-orange-100">
                          <div 
                            className="text-gray-700 leading-relaxed text-base"
                            dangerouslySetInnerHTML={{
                              __html: formatTextWithBold(selectedItem.consumer_behavior || 'No consumer behavior data available.')
                            }}
                          />
                        </div>
                      )}

                      {activeDetailTab === 'marketing' && (
                        <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-lg p-6 border border-orange-100">
                          <div 
                            className="text-gray-700 leading-relaxed text-base"
                            dangerouslySetInnerHTML={{
                              __html: formatTextWithBold(selectedItem.marketing_angle || 'No marketing information available.')
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-center p-8">
              <div className="max-w-sm">
                <div className="w-20 h-20 bg-black-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Select an Event</h3>
                <p className="text-gray-600 text-sm">Choose an event or holiday to view details and marketing opportunities.</p>
              </div>
            </div>
          )}
        </div>
        </div>
      </div>
    </div>
  );
}
